import type { Flatfile } from '@flatfile/api-internal'
import { create } from 'zustand'
import { useCallback, useMemo } from 'react'
import { Identity } from '../api/types/Identity'

type HasPermission = ({
  resource,
  permissionKey,
}: {
  resource: Flatfile.Space | Flatfile.Environment | Identity['account'] | null
  permissionKey: string
}) => boolean

export function useIdentity(): {
  identity: Identity
  setIdentity: IdentityState['setIdentity']
  isInitialized: boolean
  hasPermission: HasPermission
} {
  const identity = useIdentityStore((v) => v.identity)
  const setter = useIdentityStore((v) => v.setIdentity)
  const isInitialized = useMemo(() => Object.keys(identity).length > 0, [identity])

  const checkKeyExists = (keyToCheck: string, keys: string[]) => {
    const firstKey = keys[0]
    const firstObject = identity.permissions[firstKey]
    return keyToCheck in firstObject
  }

  const hasPermission: HasPermission = useCallback(
    ({ resource, permissionKey }) => {
      if (!resource) return false
      const keys = Object.keys(identity.permissions)
      //this will never happen in prod, but is useful locally until we move new dash into mono and can import they types
      if (keys.length && !checkKeyExists(permissionKey, keys)) {
        throw new Error('permissionKey not found')
      }

      // Direct check for the resource in permissions
      if (resource.id in identity.permissions) {
        return !!identity.permissions[resource.id][permissionKey]
      }

      // If the resource is a Space, try checking its Environment's permissions
      if ('environmentId' in resource) {
        const envId = resource.environmentId
        if (envId && envId in identity.permissions) {
          return !!identity.permissions[envId][permissionKey]
        }
      }

      // If the resource is an Environment (or a Space without permissions at its level or it's Environment level), try checking its Account's permissions
      if ('accountId' in resource) {
        const accountId = resource.accountId
        if (accountId in identity.permissions) {
          return !!identity.permissions[accountId][permissionKey]
        }
      }

      // Lastly, fall back to checking the permissions of the current account or return false
      return !!identity.permissions[identity.account.id]?.[permissionKey]
    },
    [identity.permissions, identity.account?.id]
  )

  return { identity, setIdentity: setter, isInitialized, hasPermission }
}

interface IdentityState {
  identity: Identity
  setIdentity: (identity: Identity) => void
  addEnvironment: (environment: Flatfile.Environment) => void
}

export const useIdentityStore = create<IdentityState>((set, get) => ({
  identity: {} as Identity,
  setIdentity: (identity: Identity) => set({ identity }),
  addEnvironment: (environment: Flatfile.Environment) => {
    const currentIdentity = get().identity
    set({ identity: { ...currentIdentity, environments: [...currentIdentity.environments, environment] } })
  },
}))
